import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Unified button system.
 *
 * Each variant has ONE clear purpose. Pick by intent, not by looks:
 *  - primary    : the single most important action on a page/section (CTAs)
 *  - secondary  : supporting action that sits next to a primary
 *  - tertiary   : low-emphasis / inline / navigational
 *  - accept     : affirmative confirm (e.g. cookie "Accept all", "Save")
 *  - decline    : reject / dismiss / cancel (e.g. cookie "Reject")
 *  - icon       : icon-only floating/utility buttons
 *  - destructive: dangerous, irreversible action
 *  - link       : pure text link styled as a button
 *
 * Backward-compat aliases (do not use in new code):
 *  - default -> primary
 *  - hero    -> primary
 *  - outline -> secondary
 *  - ghost   -> tertiary
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium",
    "ring-offset-background transition-all duration-200",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "active:scale-[0.98]",
    "disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_24px_hsl(var(--primary)/0.45)]",
        secondary:
          "border-[1.5px] border-primary/60 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary",
        tertiary:
          "bg-transparent text-primary hover:bg-primary/10",
        accept:
          "bg-primary text-primary-foreground font-semibold hover:bg-primary/90 hover:shadow-[0_0_24px_hsl(var(--primary)/0.45)]",
        decline:
          "border-[1.5px] border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground hover:border-muted-foreground/40",
        icon:
          "rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 hover:shadow-[0_0_24px_hsl(var(--primary)/0.5)]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        link:
          "text-primary underline-offset-4 hover:underline active:scale-100",

        // ---- backward-compat aliases ----
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_24px_hsl(var(--primary)/0.45)]",
        hero:
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_24px_hsl(var(--primary)/0.45)]",
        outline:
          "border-[1.5px] border-primary/60 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary",
        ghost:
          "bg-transparent text-primary hover:bg-primary/10",
      },
      size: {
        sm: "h-9 rounded-md px-3 text-xs",
        default: "h-10 px-5 py-2",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-md px-10 text-base",
        icon: "h-12 w-12 p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
